/**
 * Reported Errors — the destination of the "Report" action.
 *
 * When a user taps "Report" on a destructive toast (or an ErrorBoundary
 * crashes), the client beacons the error to POST /client-errors, which the
 * backend persists. This tab lists them newest-first so a human actually sees
 * what users are hitting — instead of it dying in a log stream.
 *
 * Each row EXPANDS to the full report. The collapsed line answers "what broke,
 * where, how often"; the expansion answers "what do I do about it" — the failed
 * call's method, path and status, the screen it happened on, the reporter, the
 * exact timestamp, the report id (to join against Cloud Logging), and the React
 * component stack for a render crash. Before this, a row carried a message and
 * a browser path and nothing else was reachable: an API-error report rendered a
 * sentence with no endpoint, no status, and nothing to click.
 *
 * Four async states (per the frontend resilience rules): error / loading /
 * empty / data.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useClientErrors,
  useClientErrorTriage,
  useAppendTriageEvent,
  usePostTriageComment,
} from '@/hooks/useAdminClientErrors';
import type {
  ClientErrorReport,
  ReportDiagnostics,
  ReportSessionRecord,
  TriagePrincipal,
  TriageStatus,
  TriageEvent,
} from '@/api/endpoints/admin';
import type { LogEntry } from '@/utils/logger';
import { RefreshCw, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConfigStore } from '@/stores/config';
import { safeFormatDistanceToNow, dateFnsLocale } from '@/utils/dates';

const TRIAGE_STATUSES: TriageStatus[] = ['open', 'in_progress', 'done', 'cancelled'];

/** "agent:chat" → the label an admin reads. */
function principalLabel(p: Pick<TriagePrincipal, 'type' | 'id'>): string {
  return `${p.type}:${p.id}`;
}

/**
 * One line of the append-only history, localized.
 *
 * The status here is the same wire enum the buttons render, so it goes through
 * the SAME `statuses.*` keys rather than being printed raw — otherwise the
 * history reads `· done` in German next to a button labelled `Erledigt`, which
 * is the same value spelled two ways on one screen.
 *
 * The principal (`agent:chat`, `channel:platform-alerts`) is deliberately NOT
 * translated: it is an identifier the admin types back into the picker and
 * greps the logs for, not prose.
 */
function eventLine(e: TriageEvent, t: TFunction): string {
  if (e.event_type === 'assigned') {
    return `→ ${e.assignee_type}:${e.assignee_id}`;
  }
  if (e.event_type === 'status_changed') {
    return `· ${t(`admin.reportedErrors.triage.statuses.${e.status}`)}`;
  }
  return `· ${t('admin.reportedErrors.triage.unassignedEvent')}`;
}

/** A 5xx is ours, a 4xx is the caller's — worth telling apart at a glance. */
function statusToneClass(status: number): string {
  return status >= 500
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-amber-200 bg-amber-50 text-amber-800';
}

/** One line of the console ring, as the card shows it — mono, one per entry,
 *  newest last so it reads like the console it came from. */
function ConsoleLine({ entry }: { entry: LogEntry }) {
  const tone =
    entry.level === 'error'
      ? 'text-red-700'
      : entry.level === 'warn'
        ? 'text-amber-700'
        : 'text-neutral-500';
  return (
    <div className="whitespace-pre-wrap">
      <span className="text-neutral-400">{entry.t}</span>{' '}
      <span className={tone}>{entry.level.toUpperCase()}</span>{' '}
      <span className="text-neutral-500">[{entry.ns}]</span> {entry.msg}
      {entry.data !== undefined && (
        <>
          {' '}
          <span className="text-neutral-400">{entry.data}</span>
        </>
      )}
    </div>
  );
}

/** One record of the failed turn — the payload verbatim, the type naming what
 *  it is; the turn_failure record is the one this whole view exists to reach. */
function TurnRecordLine({ record }: { record: ReportSessionRecord }) {
  const failed = record.type === 'com.ai4nr.session.message.turn_failure';
  return (
    <li
      className={`rounded p-2 ${
        failed ? 'border border-red-200 bg-red-50' : 'bg-neutral-50'
      }`}
    >
      <p className="font-mono text-[11px] text-neutral-500">
        seq {record.seq} · {record.type ?? record.role ?? 'record'}
        {record.author ? ` · ${record.author.performer}` : ''}
      </p>
      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-neutral-700 select-text">
        {JSON.stringify(record.payload, null, 2)}
      </pre>
    </li>
  );
}

/**
 * The server side of the failure, as the triage fold reads it back: the
 * conversation (agent, subject, state), the turn's own records, and the
 * pinned version's rendered prompt. Each piece that failed its read says so —
 * a refused read degrades visibly, never silently.
 */
function DiagnosticsPanel({ diag }: { diag: ReportDiagnostics }) {
  const { t } = useTranslation();
  if (diag.unavailable) {
    return (
      <p className="text-xs text-neutral-400">
        {t('admin.reportedErrors.diagnostics.unavailable', { reason: diag.unavailable })}
      </p>
    );
  }
  const session = diag.session;
  return (
    <div className="space-y-3">
      {session && (
        <div className="rounded bg-neutral-50 p-2 text-xs">
          {session.error ? (
            <p className="text-amber-700">
              {t('admin.reportedErrors.diagnostics.sessionReadError', { reason: session.error })}
            </p>
          ) : (
            <p className="font-mono text-neutral-700">
              {session.agent ? `${session.agent.slug}@${session.agent.version}` : 'no agent'}
              {session.state ? ` · ${session.state}` : ''}
              {session.subject ? ` · ${session.subject.type}:${session.subject.id}` : ''}
            </p>
          )}
          {session.title && <p className="mt-0.5 text-neutral-500">{session.title}</p>}
        </div>
      )}
      {diag.turn && (
        <div>
          <p className="mb-1 text-xs font-medium text-neutral-500">
            {t('admin.reportedErrors.diagnostics.turnRecords')}
          </p>
          {diag.turn.error ? (
            <p className="text-xs text-amber-700">{diag.turn.error}</p>
          ) : (
            <ul className="space-y-1.5">
              {diag.turn.records.map((r) => (
                <TurnRecordLine key={r.id} record={r} />
              ))}
            </ul>
          )}
        </div>
      )}
      {diag.prompt && (
        <div>
          <p className="mb-1 text-xs font-medium text-neutral-500">
            {t('admin.reportedErrors.diagnostics.prompt')}
          </p>
          {diag.prompt.error ? (
            <p className="text-xs text-amber-700">{diag.prompt.error}</p>
          ) : (
            <details>
              <summary className="cursor-pointer text-[11px] text-neutral-400">
                {t('admin.reportedErrors.diagnostics.promptNote')}
              </summary>
              <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-neutral-50 p-2 text-[11px] leading-relaxed text-neutral-600 select-text">
                {diag.prompt.rendered}
              </pre>
              {diag.prompt.layers && diag.prompt.layers.length > 0 && (
                <p className="mt-1 font-mono text-[11px] text-neutral-400">
                  {diag.prompt.layers.map((l) => `${l.name}@${l.version}`).join(' · ')}
                </p>
              )}
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/** One label/value line of the expanded detail. Values are selectable so an
 *  engineer can copy a path or an id straight out of the card. */
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 py-1">
      <dt className="w-28 shrink-0 text-xs text-neutral-500">{label}</dt>
      <dd className="min-w-0 flex-1 break-all font-mono text-xs text-neutral-700 select-text">
        {value}
      </dd>
    </div>
  );
}

/**
 * Triage controls + history for one report. Mounted only while the card is
 * open, so the list does not fetch a history per row.
 *
 * Assignees come from three places, deliberately: agents from the config
 * catalogue the app already loads, channels from this report's own response
 * (the server ships names only — a webhook URL never reaches the browser), and
 * users by pasting an id, since a staff picker here would mean loading the
 * whole directory into an error tab.
 */
function TriagePanel({ reportId }: { reportId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useClientErrorTriage(reportId, true);
  const appendEvent = useAppendTriageEvent(reportId);
  const postComment = usePostTriageComment(reportId);
  const agents = useConfigStore((s) => s.config?.agents ?? []);
  const [reply, setReply] = useState('');

  if (isLoading) return <Skeleton className="mt-3 h-24 w-full rounded" />;
  if (isError || !data) {
    return <p className="mt-3 text-xs text-red-600">{t('admin.reportedErrors.triage.loadError')}</p>;
  }

  const assignment = data.assignment;
  const busy = appendEvent.isPending || postComment.isPending;

  const assign = (type: TriagePrincipal['type'], id: string) => {
    if (!id) return;
    appendEvent.mutate({ event_type: 'assigned', assignee_type: type, assignee_id: id });
  };

  return (
    <div className="mt-4 space-y-4 border-t border-neutral-100 pt-3">
      {/* current owner + status */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-neutral-500">{t('admin.reportedErrors.triage.owner')}</span>
        {assignment ? (
          <>
            <span className="rounded-full border border-neutral-200 bg-neutral-100 px-2 py-0.5 font-mono text-xs text-neutral-700">
              {principalLabel(assignment.assignee)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              className="h-6 px-2 text-xs"
              onClick={() => appendEvent.mutate({ event_type: 'unassigned' })}
            >
              {t('admin.reportedErrors.triage.unassign')}
            </Button>
          </>
        ) : (
          <span className="text-xs text-neutral-400">
            {t('admin.reportedErrors.triage.unassigned')}
          </span>
        )}
      </div>

      {/* status — only meaningful once someone owns it; the server 409s otherwise */}
      {assignment && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-neutral-500">{t('admin.reportedErrors.triage.status')}</span>
          {TRIAGE_STATUSES.map((s) => (
            <Button
              key={s}
              variant={assignment.status === s ? 'default' : 'outline'}
              size="sm"
              disabled={busy || assignment.status === s}
              className="h-6 px-2 text-xs"
              onClick={() => appendEvent.mutate({ event_type: 'status_changed', status: s })}
            >
              {t(`admin.reportedErrors.triage.statuses.${s}`)}
            </Button>
          ))}
        </div>
      )}

      {/* assign to an agent or a channel */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-neutral-500">{t('admin.reportedErrors.triage.assignTo')}</span>
        <select
          className="rounded border border-neutral-200 bg-white px-2 py-1 text-xs"
          disabled={busy}
          value=""
          onChange={(e) => {
            const [type, ...rest] = e.target.value.split(':');
            assign(type as TriagePrincipal['type'], rest.join(':'));
          }}
        >
          <option value="">{t('admin.reportedErrors.triage.pick')}</option>
          {agents.length > 0 && (
            <optgroup label={t('admin.reportedErrors.triage.agents')}>
              {agents.map((a) => (
                <option key={a.slug} value={`agent:${a.slug}`}>
                  {a.name ?? a.slug}
                </option>
              ))}
            </optgroup>
          )}
          {data.channels.length > 0 && (
            <optgroup label={t('admin.reportedErrors.triage.channels')}>
              {data.channels.map((c) => (
                <option key={c.name} value={`channel:${c.name}`}>
                  {c.name} ({c.kind})
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      {/* under the hood — the server side of the failure, when the report
          names a conversation */}
      {data.diagnostics && (
        <div>
          <p className="mb-1 text-xs font-medium text-neutral-500">
            {t('admin.reportedErrors.diagnostics.title')}
          </p>
          <DiagnosticsPanel diag={data.diagnostics} />
        </div>
      )}

      {/* history — the record; the owner above is derived from it */}
      {data.events.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-neutral-500">
            {t('admin.reportedErrors.triage.history')}
          </p>
          <ul className="space-y-0.5">
            {data.events.map((e) => (
              <li key={e.id} className="font-mono text-[11px] text-neutral-600">
                {new Date(e.created_at).toLocaleString()} {eventLine(e, t)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* thread */}
      <div>
        <p className="mb-1 text-xs font-medium text-neutral-500">
          {t('admin.reportedErrors.triage.thread')}
        </p>
        {data.comments.items.length === 0 ? (
          <p className="text-xs text-neutral-400">{t('admin.reportedErrors.triage.noReplies')}</p>
        ) : (
          <ul className="space-y-1.5">
            {data.comments.items.map((c) => (
              <li key={c.id} className="rounded bg-neutral-50 p-2">
                <p className="text-xs text-neutral-700">{c.body}</p>
                <p className="mt-0.5 text-[11px] text-neutral-400">
                  {principalLabel(c.author)} · {new Date(c.created_at).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex gap-2">
          <input
            className="min-w-0 flex-1 rounded border border-neutral-200 px-2 py-1 text-xs"
            placeholder={t('admin.reportedErrors.triage.replyPlaceholder')}
            value={reply}
            disabled={busy}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && reply.trim()) {
                postComment.mutate(reply.trim(), { onSuccess: () => setReply('') });
              }
            }}
          />
          <Button
            size="sm"
            className="h-7 px-3 text-xs"
            disabled={busy || !reply.trim()}
            onClick={() => postComment.mutate(reply.trim(), { onSuccess: () => setReply('') })}
          >
            {t('admin.reportedErrors.triage.reply')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReportRow({ report }: { report: ClientErrorReport }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // A report is "about a call" only when the beacon carried a path. Older
  // reports (and render crashes) have none — we render the request block only
  // when it is real, never a placeholder that would read as a real endpoint.
  const hasRequest = Boolean(report.request_path);

  return (
    <li className="rounded-lg border border-neutral-200 bg-white">
      <details className="group" onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary className="cursor-pointer list-none p-4 [&::-webkit-details-marker]:hidden">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-neutral-900 break-words">
                {report.message}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700">
                  {report.context}
                </span>
                {report.error_code && (
                  <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                    {report.error_code}
                  </span>
                )}
                {hasRequest && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-xs font-medium ${statusToneClass(
                      report.request_status ?? 0,
                    )}`}
                  >
                    {report.request_method} {report.request_path}
                    {report.request_status ? ` · ${report.request_status}` : ''}
                  </span>
                )}
                {report.session_id && (
                  <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 font-mono text-xs font-medium text-blue-700">
                    {report.agent_slug
                      ? `${report.agent_slug}${report.agent_version ? `@${report.agent_version}` : ''} · `
                      : ''}
                    {t('admin.reportedErrors.detail.session')} seq {report.turn_seq ?? '?'}
                  </span>
                )}
                {report.url && (
                  <span className="truncate text-xs text-neutral-500" title={report.url}>
                    {report.url}
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {report.author_name
                  ? t('admin.reportedErrors.reportedBy', { name: report.author_name })
                  : t('admin.reportedErrors.reporterUnknown')}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <time
                className="whitespace-nowrap text-xs text-neutral-400"
                dateTime={report.created_at}
                title={new Date(report.created_at).toLocaleString()}
              >
                {safeFormatDistanceToNow(report.created_at, {
                  addSuffix: true,
                  locale: dateFnsLocale(i18n.language),
                })}
              </time>
              <ChevronRight
                className="h-4 w-4 text-neutral-400 transition-transform group-open:rotate-90"
                aria-hidden
              />
            </div>
          </div>
        </summary>

        <div className="border-t border-neutral-100 px-4 pt-3 pb-4">
          <dl className="divide-y divide-neutral-50">
            {hasRequest && (
              <>
                <DetailRow
                  label={t('admin.reportedErrors.detail.request')}
                  value={`${report.request_method ?? ''} ${report.request_path ?? ''}`.trim()}
                />
                {report.request_status !== undefined && (
                  <DetailRow
                    label={t('admin.reportedErrors.detail.status')}
                    value={String(report.request_status)}
                  />
                )}
              </>
            )}
            {report.request_id && (
              <DetailRow
                label={t('admin.reportedErrors.detail.requestId')}
                value={report.request_id}
              />
            )}
            {report.error_code && (
              <DetailRow
                label={t('admin.reportedErrors.detail.errorCode')}
                value={report.error_code}
              />
            )}
            {report.session_id && (
              <DetailRow
                label={t('admin.reportedErrors.detail.session')}
                value={report.session_id}
              />
            )}
            {report.turn_seq !== undefined && (
              <DetailRow
                label={t('admin.reportedErrors.detail.turn')}
                value={String(report.turn_seq)}
              />
            )}
            {report.agent_slug && (
              <DetailRow
                label={t('admin.reportedErrors.detail.agent')}
                value={`${report.agent_slug}${report.agent_version ? `@${report.agent_version}` : ''}`}
              />
            )}
            <DetailRow
              label={t('admin.reportedErrors.detail.origin')}
              value={report.context}
            />
            {report.url && (
              <DetailRow label={t('admin.reportedErrors.detail.screen')} value={report.url} />
            )}
            <DetailRow
              label={t('admin.reportedErrors.detail.time')}
              value={new Date(report.created_at).toLocaleString()}
            />
            <DetailRow label={t('admin.reportedErrors.detail.reportId')} value={report.id} />
          </dl>

          {report.component_stack && (
            <div className="mt-3">
              <p className="mb-1 text-xs font-medium text-neutral-500">
                {t('admin.reportedErrors.componentStack')}
              </p>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-neutral-50 p-2 text-[11px] leading-relaxed text-neutral-600 select-text">
                {report.component_stack}
              </pre>
            </div>
          )}

          {report.console_log && report.console_log.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-xs font-medium text-neutral-500">
                {t('admin.reportedErrors.consoleLog')}
              </p>
              <pre className="max-h-48 overflow-auto rounded bg-neutral-50 p-2 font-mono text-[11px] leading-relaxed text-neutral-600 select-text">
                {report.console_log.map((entry, i) => (
                  <ConsoleLine key={`${entry.t}-${i}`} entry={entry} />
                ))}
              </pre>
            </div>
          )}

          {!hasRequest && !report.component_stack && !report.session_id && (
            <p className="mt-3 text-xs text-neutral-400">
              {t('admin.reportedErrors.detail.noTrace')}
            </p>
          )}

          {open && <TriagePanel reportId={report.id} />}
        </div>
      </details>
    </li>
  );
}

export function ReportedErrors() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch, isFetching } = useClientErrors();
  const reports = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500">{t('admin.reportedErrors.intro')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          {t('admin.reportedErrors.refresh')}
        </Button>
      </div>

      {isError ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <AlertTriangle className="h-6 w-6 text-red-500" />
          <p className="text-sm text-red-700">
            {t('admin.reportedErrors.loadError')}
            {error instanceof Error ? `: ${error.message}` : ''}
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {t('actions.retry')}
          </Button>
        </div>
      ) : isLoading ? (
        <ul className="space-y-3">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </ul>
      ) : reports.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-200 p-12 text-center">
          <p className="text-sm text-neutral-500">{t('admin.reportedErrors.empty')}</p>
        </div>
      ) : (
        <ScrollArea className="h-[calc(100vh-280px)]">
          <ul className="space-y-3 pr-3">
            {reports.map((r) => (
              <ReportRow key={r.id} report={r} />
            ))}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}
